// The row actions, as icons.
//
// Edit and Delete used to be words stacked at the right of every row, which put
// two pieces of English next to every horse, owner, rider and provider — a lot
// of ink spent saying the same two things over and over. Icons say them once.
//
// Every one carries an aria-label and a title, so the meaning is still there for
// a screen reader and for anyone who hovers. An icon-only button without a label
// is a button nobody can name.
//
// 20x20 viewBox, 1.6 stroke, currentColor throughout — so a button sets the
// color and the icon follows, and a disabled row grays out as a whole.

const BASE = 'shrink-0';

function Svg({ children, className = '' }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      className={`${BASE} ${className || 'w-4 h-4'}`}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function PencilIcon({ className }) {
  return (
    <Svg className={className}>
      <path
        d="M13.5 3.5a2.12 2.12 0 0 1 3 3L7 16l-4 1 1-4 9.5-9.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

export function TrashIcon({ className }) {
  return (
    <Svg className={className}>
      <path
        d="M3.5 5.5h13M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5M5 5.5 5.8 16a1 1 0 0 0 1 .9h6.4a1 1 0 0 0 1-.9L15 5.5M8.5 9v4.5M11.5 9v4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Cover photo. Filled once it is one, outlined while it merely could be. */
export function StarIcon({ className, filled = false }) {
  return (
    <Svg className={className}>
      <path
        d="M10 2.8l2.2 4.6 5 .7-3.6 3.5.85 5-4.45-2.35L5.55 16.6l.85-5L2.8 8.1l5-.7L10 2.8Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill={filled ? 'currentColor' : 'none'}
      />
    </Svg>
  );
}

/** Duplicate — used for "repeat this pattern over the next stretch". */
export function CopyIcon({ className }) {
  return (
    <Svg className={className}>
      <rect x="7" y="7" width="9.5" height="9.5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M13 4.5A1.5 1.5 0 0 0 11.5 3h-7A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/**
 * An icon button sized for a fingertip.
 *
 * The visible mark is 16px but the target is 32 — an icon that replaces a word
 * has to stay as easy to hit as the word was, and a barn uses this on a phone
 * with gloves on.
 */
export function IconButton({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  tone = 'default',
  as: Tag = 'button',
  // Merged rather than spread through `rest`, which would let a caller passing
  // className silently replace the sizing, color and focus styling with two
  // utility classes.
  className = '',
  ...rest
}) {
  const tones = {
    default: 'text-ink-500 hover:text-ink-900 hover:bg-ink-100',
    danger: 'text-rose-600 hover:text-rose-800 hover:bg-rose-50',
    // For an action that exists but isn't allowed right now — the reason lives
    // in the title, which is why it stays rendered rather than disappearing.
    muted: 'text-ink-300 cursor-not-allowed',
  };

  return (
    <Tag
      {...(Tag === 'button' ? { type: 'button', disabled } : {})}
      onClick={disabled ? undefined : onClick}
      aria-label={label}
      title={label}
      className={
        'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ' +
        (disabled && tone !== 'muted' ? 'text-ink-300 ' : tones[tone] + ' ') +
        className
      }
      {...rest}
    >
      <Icon />
    </Tag>
  );
}
